import React, { useState, useRef, useEffect, useMemo } from "react";
import { Download, MailOpen, X, FileText, Printer, Paperclip, ChevronDown, ChevronUp, ShieldCheck, Check, Loader2, AlertTriangle, Clock, RefreshCw, Trash2, BellPlus, Video } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import * as api from "../api";
import { API_URL } from "../config";
import { evidenziaTesto, evidenziaDoc } from "../evidenzia";
import FilePreview, { tipoAnteprima } from "./FilePreview";
import usePinchZoom, { leggiZoom } from "../usePinchZoom";
import { scrittaDaMe, destinatarioBreve, statoOutbox } from "../intestazione";
import { Destinatari } from "./Destinatari";
import { prefetchEmail } from "../attachCache";
import { scaricaAllegatoRemoto, scaricaBlob, stampaAllegato } from "../attachActions";
import { LetturaBox } from "./Lettura";

function downloadDemo(nome) {
  scaricaBlob(new Blob(["Documento demo - " + nome], { type: "application/pdf" }), nome);
  toast.success("Download avviato: " + nome);
}

function isRemote(email, allegato) {
  return email?.id?.includes("|") && allegato?.part !== undefined;
}

function scaricaAllegato(email, allegato) {
  if (isRemote(email, allegato)) scaricaAllegatoRemoto(email.id, allegato);
  else downloadDemo(allegato.nome);
}

// Anteprima allegato a tutto schermo: immagini, PDF inline, scheda con "Scarica" per gli altri formati
function AttachmentPreview({ allegato, email, onClose }) {
  useEffect(() => {
    if (!allegato) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allegato, onClose]);

  if (!allegato) return null;
  const remote = isRemote(email, allegato);
  const url = remote ? api.attachmentUrl(email.id, allegato.part) : null;
  const stampabile = ["pdf", "img", "text", "html", "docx", "sheet"].includes(tipoAnteprima(allegato.tipo));
  const badge = (allegato.tipo || "FILE").toUpperCase().slice(0, 4);
  return (
    <div className="preview-fs" role="dialog" aria-modal="true" data-testid="attachment-preview">
      <div className="preview-fs-head">
        <span className="pdf-badge">{badge}</span>
        <span className="preview-fs-name" title={allegato.nome}>{allegato.nome}</span>
        <span className="preview-fs-size">{allegato.dimensione}</span>
        {remote && stampabile && (
          <button
            className="icon-btn preview-fs-btn con-tip"
            onClick={() => stampaAllegato(email.id, allegato)}
            aria-label="Stampa"
            data-tip="Stampa"
            data-testid="preview-print"
          >
            <Printer size={20} strokeWidth={2.4} />
          </button>
        )}
        <button
          className="icon-btn preview-fs-btn con-tip"
          onClick={() => scaricaAllegato(email, allegato)}
          aria-label="Scarica"
          data-tip="Scarica"
          data-testid="preview-download"
        >
          <Download size={20} strokeWidth={2.4} />
        </button>
        <button className="icon-btn preview-fs-btn preview-fs-close con-tip" onClick={onClose} aria-label="Chiudi anteprima" data-tip="Chiudi (Esc)" data-testid="preview-close">
          <X size={26} strokeWidth={2.8} />
        </button>
      </div>

      <div className="preview-fs-body" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {remote && <FilePreview id={email.id} part={allegato.part} url={url} tipo={allegato.tipo} nome={allegato.nome} dimensione={allegato.dimensione} onScarica={() => scaricaAllegato(email, allegato)} onStampa={() => stampaAllegato(email.id, allegato)} />}
        {!remote && (
          <div className="preview-fs-card" data-testid="pdf-page">
            <div className="preview-fs-icon">
              <FileText size={56} strokeWidth={1.8} />
              <span className="pdf-badge">{badge}</span>
            </div>
            <div className="preview-fs-card-name">{allegato.nome}</div>
            <div className="preview-fs-card-meta">
              {allegato.dimensione} · Da: {email?.mittente} · {email?.data}
            </div>
            <p className="preview-fs-card-text">Anteprima non disponibile: questa email non proviene da un account collegato.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Corpo HTML isolato in iframe sandbox con CSS responsive (desktop 800px, tablet 600px, mobile colonna singola)
const API_BASE = API_URL;
export function proxyImages(html, email) {
  const tok = encodeURIComponent(localStorage.getItem("mailapp_token_v1") || "");
  // immagini inline grandi (cid:) non incorporate nel corpo: arrivano dall'endpoint allegati
  const cidParte = new Map((email?.allegati || []).filter((a) => a.cid).map((a) => [a.cid, a.part]));
  const cidUrl = (cid) => {
    const part = cidParte.get(decodeURIComponent(cid));
    return part === undefined || !email?.id ? null : `${API_BASE}/emails/attachment?id=${encodeURIComponent(email.id)}&part=${part}&token=${tok}`;
  };
  // negli attributi HTML la & è scritta &amp; (o &#38;): va decodificata, altrimenti i CDN con URL firmati (fbcdn, instagram…) rispondono 403
  const deent = (u) => u.replace(/&amp;/gi, "&").replace(/&#0*38;/g, "&").replace(/&#x0*26;/gi, "&").replace(/&quot;/gi, '"').replace(/&#0*39;/g, "'");
  const abs = (u) => (u.startsWith("//") ? "https:" + u : u);
  const prox = (u) => `${API_BASE}/img?url=${encodeURIComponent(abs(deent(u)))}&token=${tok}`;
  return html
    // immagini "lazy" (data-src / data-original) → src normale, così si caricano tutte subito
    .replace(/<img\b([^>]*?)\sdata-(?:src|original|lazy-src)\s*=\s*(["'])((?:https?:)?\/\/[^"']+)\2/gi, (m, pre, q, u) => `<img${pre} src=${q}${u}${q}`)
    .replace(/(<img\b[^>]*?\s(?:src|background)\s*=\s*["'])((?:https?:)?\/\/[^"']+)(["'])/gi, (m, a, u, z) => a + prox(u) + z)
    // srcset: ogni URL passa dal proxy
    .replace(/(<(?:img|source)\b[^>]*?\ssrcset\s*=\s*["'])([^"']+)(["'])/gi, (m, a, set, z) =>
      a + set.split(",").map((p) => { const [u, d] = p.trim().split(/\s+/); return (/^(https?:)?\/\//i.test(u) ? prox(u) : u) + (d ? " " + d : ""); }).join(", ") + z)
    .replace(/(<(?:td|th|table|body|div|tr)\b[^>]*?\sbackground\s*=\s*["'])((?:https?:)?\/\/[^"']+)(["'])/gi, (m, a, u, z) => a + prox(u) + z)
    .replace(/url\((["']?)((?:https?:)?\/\/[^"')]+)\1\)/gi, (m, q, u) => `url(${q}${prox(u)}${q})`)
    .replace(/<img\b([^>]*?)\sloading\s*=\s*["']lazy["']/gi, "<img$1")
    .replace(/(<img\b[^>]*?\ssrc\s*=\s*["'])cid:([^"']+)(["'])/gi, (m, a, cid, z) => { const u = cidUrl(cid); return u ? a + u + z : m; });
}

// Interlinea dichiarata più stretta del carattere (es. font-size:13px con line-height:12px): le righe si
// sovrappongono → la porto al minimo leggibile. Una sola volta per documento, prima della misura
function allargaInterlinea(d) {
  if (d.body.dataset.interlinea) return;
  d.body.dataset.interlinea = "1";
  const w = d.defaultView;
  d.querySelectorAll(".posta-scala *").forEach((el) => {
    if (!el.firstChild || el.children.length === el.childNodes.length) return;
    const cs = w.getComputedStyle(el);
    const fs = parseFloat(cs.fontSize);
    const lh = parseFloat(cs.lineHeight);
    if (fs > 0 && lh > 0 && lh < fs * 1.15) el.style.setProperty("line-height", "1.25", "important");
  });
}

function HtmlBody({ html: rawHtml, email, onCaricato, zoom = 1 }) {
  const ref = useRef(null);
  const caricato = useRef(false);
  const [fallback, setFallback] = useState(false);
  const html = useMemo(() => proxyImages(rawHtml, email), [rawHtml, email?.id, email?.allegati]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const doc = useMemo(() => `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>
    html,body{margin:0;padding:0;-webkit-text-size-adjust:100%;text-size-adjust:100%;}
    .posta-scala *{-webkit-text-size-adjust:100%;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.5;color:#111;background:#fff;overflow-x:hidden;}
    .posta-wrap{width:100%;margin:0;padding:8px 0;box-sizing:border-box;overflow:hidden;-webkit-overflow-scrolling:touch;touch-action:pinch-zoom pan-x pan-y;}
    .posta-scala{width:100%;transform-origin:0 0;}
    .posta-wrap [style*="position:fixed"],.posta-wrap [style*="position: fixed"]{position:static !important;}
    img{max-width:100%;height:auto;}
    pre{white-space:pre-wrap;}
  </style></head><body><div class="posta-wrap"><div class="posta-scala">${html}</div></div></body></html>`, [html]);

  // Resa "come online": il layout originale della newsletter resta intatto; se è più largo del pannello
  // (es. tabelle fisse a 600px senza regole mobile) viene ridotto in scala per starci tutto, senza scroll orizzontale
  const adatta = (d) => {
    const wrap = d.querySelector(".posta-wrap");
    const scala = d.querySelector(".posta-scala");
    if (!wrap || !scala) return;
    scala.style.zoom = "1";
    scala.style.width = "100%";
    const W = wrap.clientWidth;
    let largh = scala.scrollWidth;
    d.querySelectorAll(".posta-scala *").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.right > largh) largh = Math.ceil(r.right);
    });
    fit.current = { k: largh > W + 2 ? W / largh : 1, largh: largh > W + 2 ? largh : 0 };
    applicaZoom(d);
  };

  // Scala di adattamento alla larghezza del pannello × zoom a due dita (il testo si riadatta; i layout a
  // larghezza fissa, se ingranditi oltre il pannello, scorrono in orizzontale)
  const fit = useRef({ k: 1, largh: 0 });
  const applicaZoom = (d) => {
    const wrap = d.querySelector(".posta-wrap");
    const scala = d.querySelector(".posta-scala");
    if (!wrap || !scala) return;
    const z = zoomRef.current || 1;
    if (fit.current.largh) {
      scala.style.width = fit.current.largh + "px";
      scala.style.zoom = String(fit.current.k * z);
    } else {
      scala.style.width = z > 1 ? `${100 / z}%` : "100%";
      scala.style.zoom = String(z);
    }
    wrap.style.overflowX = z > 1 ? "auto" : "hidden";
  };

  const misura = () => {
    try {
      const d = ref.current?.contentDocument;
      if (!d?.body) return;
      evidenziaDoc(d);
      allargaInterlinea(d);
      adatta(d);
      const h = d.body.scrollHeight;
      if (h > 40) ref.current.style.height = Math.min(h + 8, 40000) + "px";
    } catch (e) {
      console.debug("EmailDetail.misura: iframe non accessibile", e);
    }
  };

  // Se l'iframe non riesce a mostrare il documento (bloccato o vuoto) → HTML inline sanificato.
  // Solo a documento completo: mentre si caricano le immagini il testo c'è già e il formato originale va mantenuto
  const verificaFallback = () => {
    try {
      const d = ref.current?.contentDocument;
      if (d?.body && d.body.children.length > 0) return;
      const vuoto = !d?.body || (d.body.innerText.trim().length === 0 && d.images.length === 0 && rawHtml.replace(/<[^>]+>/g, "").trim().length > 0);
      if (vuoto) setFallback(true);
    } catch (e) {
      setFallback(true);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  });
  // zoom a due dita cambiato → riapplica scala e altezza dell'iframe
  useEffect(() => {
    if (caricato.current) misura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);
  useEffect(() => {
    setFallback(false);
    caricato.current = false;
    // Il documento srcDoc è pronto subito, ma l'evento load dell'iframe aspetta TUTTE le immagini (proxy lento):
    // misuro appena il corpo esiste e poi a ogni variazione di altezza, così il messaggio è intero da subito
    let ro = null;
    const avvio = performance.now();
    const t = setInterval(() => {
      try {
        const d = ref.current?.contentDocument;
        if (d?.body?.children.length > 0) {
          misura();
          if (!ro && typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => misura());
            ro.observe(d.body);
          }
        }
      } catch (e) {
        console.debug("EmailDetail: misura anticipata non riuscita", e);
      }
      if (caricato.current || performance.now() - avvio > 15000) clearInterval(t);
    }, 120);
    const tf = setTimeout(() => {
      if (!caricato.current) verificaFallback();
    }, 3500);
    return () => {
      clearInterval(t);
      clearTimeout(tf);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawHtml]);

  if (fallback) {
    return (
      <div
        className="mail-inline"
        style={{ zoom }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"], ADD_ATTR: ["target"] }) }}
        data-testid="detail-body-inline"
      />
    );
  }

  return (
    <iframe
      ref={ref}
      className="mail-frame"
      title="Corpo email"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={doc}
      onLoad={() => {
        caricato.current = true;
        misura();
        verificaFallback();
        onCaricato?.();
        // immagini inserite piccole dal mittente: clic = grandezza piena / di nuovo piccola
        try {
          const d = ref.current?.contentDocument;
          d?.querySelectorAll("img[width]").forEach((img) => {
            if (img.dataset.zoomabile || parseInt(img.getAttribute("width"), 10) > 300) return;
            img.dataset.zoomabile = "1";
            img.style.cursor = "zoom-in";
            img.title = "Clic per ingrandire";
            img.addEventListener("click", (e) => {
              e.preventDefault();
              const grande = img.dataset.grande === "1";
              img.dataset.grande = grande ? "" : "1";
              img.style.width = grande ? img.getAttribute("width") + "px" : "100%";
              img.style.maxWidth = "100%";
              img.style.cursor = grande ? "zoom-in" : "zoom-out";
              setTimeout(misura, 50);
            });
          });
        } catch (e) {
          console.debug("EmailDetail: immagini zoomabili non impostate", e);
        }
        setTimeout(misura, 700);
        setTimeout(misura, 2200);
      }}
      data-testid="detail-body-frame"
    />
  );
}

// Allegati accorpati in una sola riga espandibile
function AttachmentsBox({ email, onPreview }) {
  const [open, setOpen] = useState(false);
  const n = email.allegati.length;
  if (n === 0) return null;
  return (
    <div className="attachments-box" data-testid="attachments-box">
      <button className="attachments-toggle con-tip tip-sx-int" data-tip={open ? "Nascondi allegati" : "Mostra allegati"} onClick={() => setOpen((o) => !o)} data-testid="attachments-toggle">
        <Paperclip size={18} strokeWidth={2.4} />
        <span className="attachments-count">{n === 1 ? "1 allegato" : n + " allegati"}</span>
        <span className="attachments-names">{email.allegati.map((a) => a.nome).join(", ")}</span>
        {open ? <ChevronUp size={18} strokeWidth={2.6} /> : <ChevronDown size={18} strokeWidth={2.6} />}
      </button>
      {open &&
        email.allegati.map((a, i) => (
          <button key={`${a.nome}-${a.dimensione || i}`} className="attachment-card con-tip tip-sx-int" data-tip="Apri anteprima" onClick={() => onPreview(a)} data-testid={`attachment-${i}`}>
            <span className="pdf-badge">{(a.tipo || "FILE").toUpperCase().slice(0, 4)}</span>
            <span className="attachment-name">{a.nome}</span>
            <span className="attachment-size">{a.dimensione}</span>
            <span
              className="icon-plain con-tip"
              role="button"
              tabIndex={0}
              style={{ width: 34, height: 34, minWidth: 34 }}
              data-tip="Scarica"
              aria-label="Scarica subito"
              onClick={(e) => {
                e.stopPropagation();
                scaricaAllegato(email, a);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  scaricaAllegato(email, a);
                }
              }}
              data-testid={`attachment-download-${i}`}
            >
              <Download size={20} strokeWidth={2.4} />
            </span>
          </button>
        ))}
    </div>
  );
}

// Dettagli di spedizione PEC: testo del gestore, ricevute e file di sistema, in un riquadro richiudibile
function PecBox({ email, ricevute, onPreview }) {
  const [open, setOpen] = useState(false);
  const [openRic, setOpenRic] = useState(null);
  const sistema = email.allegati_sistema || [];
  const list = (ricevute || []).filter((r) => r.id !== email.id);
  if (!email.pec_tipo && list.length === 0) return null;
  return (
    <div className="pec-box" data-testid="pec-box">
      <button className="attachments-toggle pec-toggle con-tip tip-sx-int" data-tip={open ? "Nascondi dettagli PEC" : "Mostra dettagli PEC"} onClick={() => setOpen((o) => !o)} data-testid="pec-toggle">
        <ShieldCheck size={18} strokeWidth={2.4} />
        <span className="attachments-count">Dettagli di spedizione PEC</span>
        <span className="attachments-names">
          {list.length > 0 ? list.map((r) => r.pec_ricevuta).join(" · ") : email.pec_ricevuta || "Messaggio certificato"}
        </span>
        {open ? <ChevronUp size={18} strokeWidth={2.6} /> : <ChevronDown size={18} strokeWidth={2.6} />}
      </button>
      {open && (
        <div className="pec-body">
          {list.map((r) => (
            <div key={r.id} className="pec-ricevuta" data-testid={`pec-ricevuta-${r.id}`}>
              <button className="pec-ricevuta-head" onClick={() => setOpenRic(openRic === r.id ? null : r.id)}>
                <Check size={14} strokeWidth={3} />
                <strong>{r.pec_ricevuta}</strong>
                <span className="pec-ricevuta-date">{r.data} {r.ora}</span>
                <span className="pec-ricevuta-from">{r.mittente}</span>
              </button>
              {openRic === r.id && <pre className="pec-text">{r.corpo}</pre>}
            </div>
          ))}
          {email.pec_dettagli && <pre className="pec-text" data-testid="pec-dettagli">{email.pec_dettagli}</pre>}
          {sistema.length > 0 && (
            <div className="pec-sistema">
              {sistema.map((a, i) => (
                <button
                  key={`${a.nome}-${i}`}
                  className="attach-chip con-tip"
                  onClick={() => onPreview(a)}
                  data-tip="Apri"
                  data-testid={`pec-file-${i}`}
                >
                  <FileText size={13} strokeWidth={2.4} />
                  <span className="attach-chip-name">{a.nome}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Testo "piano" che in realtà è HTML (mittenti che mettono markup nella parte text/plain): mai mostrarlo grezzo
export const pareHtml = (t) => !!t && /<\s*(html|body|div|span|p|table|tr|td|br|a|img|font|b|i|u|strong|em|ul|ol|li|h[1-6])\b[^>]*>/i.test(t.slice(0, 4000));

// Corpo in testo semplice: il fattore di zoom a due dita ingrandisce il carattere (il testo si riadatta alla larghezza)
function TestoZoomabile({ children, zoom = 1 }) {
  return (
    <div className="detail-body detail-body-zoom" style={{ fontSize: Math.round(15 * zoom * 10) / 10 }} data-testid="detail-body">
      {children}
    </div>
  );
}

// Corpo del messaggio: avviso provvisorio, HTML (anche quando arriva nella parte testo) oppure testo semplice
function Corpo({ email, zoom, onCaricato }) {
  if (email.provvisorio) {
    return <TestoZoomabile zoom={zoom}>{typeof email.provvisorio === "string" ? email.provvisorio : "Contenuto non ancora scaricato: riprova tra poco."}</TestoZoomabile>;
  }
  if (email.corpo_html) return <HtmlBody html={email.corpo_html} email={email} zoom={zoom} onCaricato={onCaricato} />;
  if (pareHtml(email.corpo)) return <HtmlBody html={email.corpo} email={email} zoom={zoom} onCaricato={onCaricato} />;
  return <TestoZoomabile zoom={zoom}>{evidenziaTesto(email.corpo)}</TestoZoomabile>;
}

export default function EmailDetail({ email, ricevute, onRetryOutbox, onDeleteOutbox, onRicordamelo, onMeet, onScriviA }) {
  const [preview, setPreview] = useState(null);
  const [frameLoad, setFrameLoad] = useState(0);
  const [zoom, setZoom] = useState(leggiZoom);
  const zoomWrapRef = useRef(null);
  const zoomScala = usePinchZoom(zoomWrapRef, setZoom, { frameLoad, chiave: `${email?.id || ""}|${email?.corpo_html ? "h" : "t"}|${email?.light ? 1 : 0}` });

  useEffect(() => {
    if (email && !email.light) prefetchEmail(email);
  }, [email]);

  if (!email) {
    return (
      <div className="detail-empty" data-testid="detail-empty">
        <MailOpen size={48} strokeWidth={1.8} />
        <span>Seleziona un'email per leggerla</span>
      </div>
    );
  }

  return (
    <div className="email-detail" data-testid="email-detail">
      <div className="detail-head">
        <div className="detail-head-info">
          {scrittaDaMe(email) && email.a ? (
            <div className="detail-sender" data-testid="detail-sender">A: {destinatarioBreve(email.a)}</div>
          ) : (
            <>
              <div className="detail-sender" data-testid="detail-sender">{email.mittente}</div>
              <a className="detail-email" href={`mailto:${email.email}`} data-testid="detail-email">
                {email.email}
              </a>
            </>
          )}
          <Destinatari etichetta="A" valore={email.a} chiave="a" onScrivi={onScriviA} />
          <Destinatari etichetta="CC" valore={email.cc} chiave="cc" onScrivi={onScriviA} />
          <Destinatari etichetta="CCN" valore={email.ccn} chiave="ccn" onScrivi={onScriviA} />
        </div>
        <div className="detail-date" data-testid="detail-date">
          <span>{email.data}</span>
          <span>{email.ora}</span>
          {onRicordamelo && email.folder !== "uscita" && email.folder !== "bozze" && (
            <div className="detail-azioni">
              <button className="detail-azione con-tip" data-tip="Crea un memo nel calendario" onClick={() => onRicordamelo(email)} data-testid="detail-ricordamelo">
                <BellPlus size={15} strokeWidth={2.5} /> Ricordamelo
              </button>
              <button className="detail-azione con-tip" data-tip="Videochiamata Google Meet con il mittente" onClick={() => onMeet(email)} data-testid="detail-meet">
                <Video size={15} strokeWidth={2.5} /> Meet
              </button>
            </div>
          )}
        </div>
      </div>

      {email.folder === "uscita" && email.outbox && (
        <div className={`outbox-box ${email.outbox.stato}`} data-testid="outbox-box">
          <div className="outbox-title">
            {email.outbox.stato === "fallita" ? <AlertTriangle size={17} strokeWidth={2.4} /> : <Clock size={17} strokeWidth={2.4} />}
            {statoOutbox(email.outbox, "lungo")}
          </div>
          <div className="outbox-err">{email.outbox.ultimo_errore}</div>
          <div className="outbox-actions">
            <button className="wf-btn primary" onClick={() => onRetryOutbox && onRetryOutbox(email.outbox)} data-testid="outbox-retry">
              <RefreshCw size={15} strokeWidth={2.4} /> Riprova ora
            </button>
            <button className="wf-btn del-profile-btn" onClick={() => onDeleteOutbox && onDeleteOutbox()} data-testid="outbox-delete">
              <Trash2 size={15} strokeWidth={2.4} /> Elimina
            </button>
          </div>
        </div>
      )}

      {email.light && !email.provvisorio && (
        <div className="ai-loading" style={{ marginTop: 14 }} data-testid="detail-loading">
          <Loader2 size={18} className="spin" /> Scaricamento del messaggio completo...
        </div>
      )}
      <PecBox email={email} ricevute={ricevute} onPreview={setPreview} />
      <LetturaBox email={email} />
      <AttachmentsBox email={email} onPreview={setPreview} />

      <div ref={zoomWrapRef} className="corpo-zoom" data-testid="detail-zoom" data-pinch={zoom.toFixed(2)}>
        <Corpo email={email} zoom={zoom} onCaricato={() => setFrameLoad((n) => n + 1)} />
      </div>


      <AttachmentPreview allegato={preview} email={email} onClose={() => setPreview(null)} />
    </div>
  );
}
